import { StylesConfig } from 'react-select';

export interface Choice {
  value: string | number | null;
  title: string;
}

export const isChoice = (v: unknown): v is Choice => {
  return !!v && (v as Choice).value !== undefined;
};

export const isDarkMode = (): boolean => {
  const cl = document.documentElement.classList;
  return cl ? cl.contains('dark') : false;
};

const lightColors = {
  bg: '#F4F4F6',
  bgFocused: '#FFFFFF',
  border: '#F4F4F6',
  borderFocused: '#32BB78',
  text: '#2F313F',
  menuBg: '#FFFFFF',
  optionSelected: '#E0FFF0',
  optionHover: '#F4F4F6',
  errorBorder: 'rgba(235, 71, 85, 1)',
};

const darkColors = {
  bg: '#404B5B',
  bgFocused: '#2A313C',
  border: '#404B5B',
  borderFocused: '#1E965C',
  text: '#FAFAFB',
  menuBg: '#404B5B',
  optionSelected: '#1E965C',
  optionHover: '#2A313C',
  errorBorder: '#DB5762',
};

export const getCustomStyles = (isDark: boolean): StylesConfig<Choice> => {
  const c = isDark ? darkColors : lightColors;
  return {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? c.bgFocused : c.bg,
      borderColor: state.isFocused ? c.borderFocused : c.border,
      borderWidth: '2px',
      boxShadow: 'none',
      padding: '8px 12px',
      ':hover': {
        ...provided[':hover'],
        borderColor: state.isFocused ? c.borderFocused : c.border,
      },
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: c.text,
      padding: 0,
      transform: state.isFocused ? 'rotate(180deg)' : 'none',
      ':hover': {
        color: c.text,
      },
      ':focus': {
        color: c.text,
      },
      ':active': {
        color: c.text,
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#999999',
      padding: '4px 8px',
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      color: '#999999',
      marginLeft: '2px',
      marginRight: '10px',
    }),
    menu: (provided) => ({
      ...provided,
      background: c.menuBg,
      position: 'absolute',
      left: 0,
      right: 0,
      zIndex: 1001,
    }),
    option: (provided, state) => ({
      ...provided,
      padding: '8px 24px',
      minHeight: '40px',
      lineHeight: '24px',
      backgroundColor: state.isSelected ? c.optionSelected : c.menuBg,
      color: c.text,
      ':hover': {
        ...provided[':hover'],
        backgroundColor: state.isSelected ? c.optionSelected : c.optionHover,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: c.text,
    }),
    input: (provided) => ({
      ...provided,
      color: c.text,
      height: '18px',
    }),
    valueContainer: (provided) => ({
      ...provided,
      paddingLeft: 0,
      paddingTop: '3px',
      paddingBottom: '3px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDark ? '#A5B0C0' : provided.color,
    }),
  };
};

export const getErrorStyles = (isDark: boolean): StylesConfig<Choice> => {
  const base = getCustomStyles(isDark);
  const c = isDark ? darkColors : lightColors;
  return {
    ...base,
    control: (provided, state) => ({
      ...provided,
      ...(base.control && base.control(provided, state)),
      borderColor: c.errorBorder,
      ':hover': {
        ...provided[':hover'],
        borderColor: c.errorBorder,
      },
    }),
  };
};
